import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Credit, Link, Media } from '@elevator/ui';
import CreditGrid from '../CreditGrid/CreditGrid';
import SectionHeader from '../SectionHeader/SectionHeader';
import useStyles from './styles';

/** Shape of submission data displayed by SubmissionDetails (e.g. from cart). */
export type SubmissionDetailsType = {
  contentTitle?: string;
  contentUrl?: string;
  contentDescription?: string;
  contentImage?: { sourceUrl: string };
  credits?: Array<{
    creditId?: string;
    name?: string;
    creditType: { typeName: string };
    user?: { avatar?: Media | null };
  }>;
};

export interface SubmissionDetailsProps {
  /** Submission data to display (title, link, description, image, credits) */
  submission: SubmissionDetailsType;
}

/**
 * SubmissionDetails displays the Submission Info and Credits sections for review.
 * Used in ReviewStep (submission flow review step). Presentational; pass submission data as props.
 *
 * Owns internal spacing between its sections so it renders correctly in isolation (e.g. Storybook)
 * and when composed; parent controls semantic wrapper and spacing to siblings.
 */
const SubmissionDetails = ({ submission }: SubmissionDetailsProps) => {
  const { classes } = useStyles();
  const {
    contentTitle,
    contentUrl,
    contentDescription,
    contentImage,
    credits = [],
  } = submission;

  return (
    <Stack spacing={2} data-component="SubmissionDetails">
      <Stack component="section" spacing={2}>
        <SectionHeader title="Content" titleVariant="h6" divider={false} />
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Submission Title
          </Typography>
          <Typography variant="body1" className={classes.fieldContent}>
            {contentTitle || '(No title)'}
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Content Link
          </Typography>
          {contentUrl ? (
            <Link
              href={contentUrl}
              className={`${classes.link} ${classes.fieldContent}`}
              aria-label={`Open content link: ${contentUrl}`}
              showExternalIcon={false}
            >
              {contentUrl}
            </Link>
          ) : (
            <Typography variant="body1" className={classes.fieldContent}>
              (No link provided)
            </Typography>
          )}
        </Stack>

        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Description
          </Typography>
          <Typography variant="body1" className={classes.fieldContent}>
            {contentDescription || '(No description)'}
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Image
          </Typography>
          {contentImage?.sourceUrl ? (
            <Link
              href={contentImage.sourceUrl}
              className={classes.link}
              aria-label="Open submission image in new tab"
              showExternalIcon={false}
            >
              <div className={classes.imageWrapper}>
                <img
                  className={classes.image}
                  src={contentImage.sourceUrl}
                  alt="Submission preview image"
                />
              </div>
            </Link>
          ) : (
            <Typography variant="body1" className={classes.fieldContent}>
              (No image attached)
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack component="section" spacing={2}>
        <SectionHeader title="Credits" titleVariant="h6" divider={false} />
        {credits?.length > 0 ? (
          <CreditGrid credits={credits as Credit[]} />
        ) : (
          <Typography variant="body1" color="text.secondary">
            (No credits)
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default SubmissionDetails;
